/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleProcessTemplate } from 'features/processTemplates/processTemplatesSlice'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, ThinSeparator } from 'styling/GlobalStyles'
import { IProcessTemplatesSingleResponse } from 'types/api/processTemplatesApi.interface'
import { IPhaseState, IProcessBindingConfigState } from 'types/operationPlans.interface'

type ProcessTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<IProcessBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<IProcessBindingConfigState>>>
}

export const ProcessTemplateType = ({ formKey, addingPhase, setAddingPhase }: ProcessTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as IProcessBindingConfigState)

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<IProcessBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as IProcessBindingConfigState,
        }))
    }

    const handleProcessSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleProcessTemplate(value.value.toString()))
        if (resp.meta.requestStatus === 'fulfilled') {
            setAddingPhase((prev) => ({
                ...prev,
                processTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    department: null,
                    location: null,
                    componentsData: (resp.payload as IProcessTemplatesSingleResponse).components.map((component) => ({
                        processTemplateComponent: { label: component.name, value: component.id },
                        componentType: component.component,
                        value: null,
                    })),
                },
            }))
        }
    }

    const handleComponentValue = (e: React.ChangeEvent<HTMLInputElement>, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.processTemplateComponent?.value === componentId ? { ...c, value: newValue } : c,
            ),
        })
    }

    return <>
        <Flex column gap='1rem' >
            <HonestSelectAsyncInput
                key={`process-template-${formKey}`}
                keyProp={2}
                defaultValue={addingPhase.processTemplate?.label}
                link='process/template/company'
                labelText='Task Process Template'
                onChange={handleProcessSelect}
                name='processTemplate'
                placeholder='Search Process Templates'
                parserFunction={parse}
                required
                invalid={!addingPhase.processTemplate?.value}
            />
            {addingPhase.processTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`process-startDate-${formKey}`}
                        labelText='Start Date'
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                </Flex>
                <HonestSelectAsyncInput
                    key={`process-location-${formKey}`}
                    keyProp={2}
                    nomarg
                    link='locations'
                    placeholder='Search locations'
                    name='location'
                    labelText='Location'
                    defaultValue={bindingConfig.location?.label}
                    onChange={(value) => updateBindingConfig({ location: value })}
                    parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                    required
                    invalid={!bindingConfig.location?.value}
                />
                <HonestSelectAsyncInput
                    key={`process-department-${formKey}`}
                    keyProp={2}
                    link='departments'
                    placeholder='Search department'
                    name='department'
                    labelText='Department'
                    defaultValue={bindingConfig.department?.label}
                    onChange={(value) => updateBindingConfig({ department: value })}
                    parserFunction={(data: any) =>
                        data.data.map((i: any) => ({ label: i.name, value: i.id }))
                    }
                />
                <ThinSeparator />
                <ItemsGrid columns={2} >
                    {bindingConfig.componentsData?.map((c) =>
                        <Input
                            value={c.value ?? ''}
                            key={c.processTemplateComponent?.value}
                            width='100%'
                            placeholder='Enter value'
                            labelText={c.processTemplateComponent?.label + ' value'}
                            name='value'
                            onChange={(e) => handleComponentValue(e, c.processTemplateComponent?.value)}
                            type='number'
                        />,
                    )}
                </ItemsGrid>
            </>}
        </Flex>

    </>
}
